// @react
import { useContext } from 'react'
// @types
import {
  GetUserQueryResult,
  UserCommunicationPreferencesUpdateMutation,
  UserCommunicationPreferencesUpdateMutationVariables,
} from 'lib/graphql/graphqlTypes'
import { CommunicationFormValues } from 'modules/common/forms/types/communication'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
// @graphQL
import { useMutation } from '@apollo/client'
import client from 'graphql/Apollo/apolloClient'
import { USER_COMMUNICATION_PREFERENCES_UPDATE } from 'graphql/mutations/user.mutation'
// @libraries / common
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { SnackbarContext } from 'modules/common/context/snackbar'
// @components
// @module components
import { validationSchema } from 'modules/common/forms/validations/communication'
import strings from 'lib/constants/strings'
import { vsprintf } from 'sprintf-js'

type Inputs = any

type SelfProps = {
  data: GetUserQueryResult['data']
}
type PropsType = SelfProps

const useCommunicationFormWrapper = <T extends Inputs>({
  data: userData,
}: PropsType) => {
  /****************************************
   * CONST
   *****************************************/

  const email = userData?.user?.contactInfo.primaryEmailAddress

  const initialValues: Partial<CommunicationFormValues> = {
    fewerEmails: userData?.user?.communicationPreferences.bimonthlyDigest
      ? 'bi-monthly'
      : '',
    isPostalMailOptedOut:
      userData?.user?.communicationPreferences?.isPostalMailOptedOut,
    receivePromotionalCommunication:
      userData?.user?.communicationPreferences.receivePromotionalCommunication,
  }
  const defaultValues = userData?.user?.communicationPreferences.newsletters.reduce(
    (acc, curr) => {
      const label = curr.newsletterGroupLabel as keyof Omit<
        CommunicationFormValues,
        | 'fewerEmails'
        | 'isPostalMailOptedOut'
        | 'receivePromotionalCommunication'
      >
      acc[label] = curr.newsletterGroup
        .filter((group) => group.value && group.key)
        .map((group: any) => group.key)
      return acc
    },
    initialValues,
  )

  /****************************
   *  HOOKS
   *****************************/

  const formMethods = useForm<CommunicationFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues,
  })
  const { control } = formMethods

  /****************************************
   * GQL HOOKS
   *****************************************/

  const [updateUserCommunicationPreferences] = useMutation<
    UserCommunicationPreferencesUpdateMutation,
    UserCommunicationPreferencesUpdateMutationVariables
  >(USER_COMMUNICATION_PREFERENCES_UPDATE)

  const snackBar = useContext(SnackbarContext)

  /****************************************
   * FUNCTIONS
   *****************************************/

  const userCommunicationPreferencesUpdateHandler = async (
    data: CommunicationFormValues,
  ): Promise<boolean> => {
    //we update the user preferences by email, so if not email, fail
    if (!email) {
      return false
    }

    const final: any[] = []
    Object.keys(data).map((item) => {
      const key = item as keyof CommunicationFormValues
      if (
        key !== 'isPostalMailOptedOut' &&
        key !== 'fewerEmails' &&
        key !== 'receivePromotionalCommunication' &&
        data[key]?.length
      ) {
        data[key]?.map((subItem) => final.push(subItem))
      }
    })

    const { errors: mutationErrors } = await updateUserCommunicationPreferences(
      {
        variables: {
          input: {
            receivePromotionalCommunication:
              data.receivePromotionalCommunication,
            universalOptOut: data.fewerEmails === 'unsubscribe',
            bimonthlyDigest: data.fewerEmails === 'bi-monthly',
            isPostalMailOptedOut: data.isPostalMailOptedOut,
            newsletterGroups: final.map((item) => {
              return {
                key: item,
                value: true,
              }
            }),
          },
        },
      },
    )

    if (!mutationErrors) {
      snackBar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
          'Communication preferences',
        ]),
        show: true,
      })

      // there's a non trivial delay between mutation and updated REST response from datasource
      // therefore we have no choice but to optimistically set cache
      const newUserComms = userData?.user?.communicationPreferences.newsletters.map(
        (item) => {
          return {
            ...item,
            newsletterGroup: item.newsletterGroup.map((subItem) => {
              return {
                ...subItem,
                // @ts-ignore
                value: data[item.newsletterGroupLabel].includes(subItem.key),
              }
            }),
          }
        },
      )

      client.cache.modify({
        id: client.cache.identify(userData?.user!),
        fields: {
          communicationPreferences(cachedData) {
            return {
              ...cachedData,
              receivePromotionalCommunication:
                data.receivePromotionalCommunication,
              bimonthlyDigest: data.fewerEmails === 'bi-monthly',
              isPostalMailOptedOut: data.isPostalMailOptedOut,
              newsletters: newUserComms,
            }
          },
        },
      })
      return true
    } else {
      snackBar.setMessage({
        type: SnackbarTypes.ERROR,
        message: strings.ERRORS.GENERIC_UPDATE_ERROR,
        show: true,
      })
      console.log('mutation error', mutationErrors)
    }
    return false
  }

  /****************************************
   * RENDER
   *****************************************/

  return {
    formMethods,
    control,
    defaultValues,
    userCommunicationPreferencesUpdateHandler,
  }
}

export default useCommunicationFormWrapper
